// Pure stat computation. Final stats = base * levelScaling.
// Champions use per-unit levelGrowth rates; enemies use flat defaults.

import type { Unit } from "./Unit";
import type { LevelGrowth } from "../data/champions";
import { ITEMS } from "../data/items";

export interface EffectiveStats {
  maxHp: number;
  ad: number;
  ap: number;
  range: number;
  moveSpeed: number;
  attackSpeed: number;
}

// Targets at level 18: HP ×5, AD ×5, AP ×5, AS unchanged.
// Formula: 1 + growth×17 = target  →  growth = (target-1)/17
const DEFAULT_GROWTH: LevelGrowth = { hp: 0.2353, ad: 0.2353, ap: 0, attackSpeed: 0 };

export function effectiveStats(unit: Unit): EffectiveStats {
  const d = unit.def;
  const g = unit.championDef?.levelGrowth ?? DEFAULT_GROWTH;
  const m = unit.level - 1;
  const ultActive = unit.olafUltTurnsRemaining > 0;
  const item = unit.item ? ITEMS[unit.item] : null;
  const yuumi = unit.yuumiBoostMult > 1 ? unit.yuumiBoostMult : 1;
  const redBuffAd = unit.redBuff ? 1.5 : 1;
  return {
    maxHp:       Math.round(d.baseHp * (1 + g.hp * m) * (item?.hpMult ?? 1) * yuumi),
    ad:          Math.round(d.baseAd * (1 + g.ad * m) * (ultActive ? 1.25 : 1) * (item?.adMult ?? 1) * yuumi * redBuffAd),
    ap:          Math.round(d.baseAp * (1 + g.ap * m) * (item?.apMult ?? 1) * yuumi),
    range:       d.range,
    moveSpeed:   d.moveSpeed,
    attackSpeed: d.baseAttackSpeed > 0 ? d.baseAttackSpeed * (1 + g.attackSpeed * m) * (ultActive ? 1.35 : 1) : 0,
  };
}

interface StatsDef {
  baseHp: number; baseAd: number; baseAp: number;
  range: number; moveSpeed: number; baseAttackSpeed: number;
}

export function effectiveStatsForLevel(def: StatsDef, level: number, growth?: LevelGrowth): EffectiveStats {
  const g = growth ?? DEFAULT_GROWTH;
  const m = level - 1;
  return {
    maxHp:       Math.round(def.baseHp * (1 + g.hp * m)),
    ad:          Math.round(def.baseAd * (1 + g.ad * m)),
    ap:          Math.round(def.baseAp * (1 + g.ap * m)),
    range:       def.range,
    moveSpeed:   def.moveSpeed,
    attackSpeed: def.baseAttackSpeed > 0 ? def.baseAttackSpeed * (1 + g.attackSpeed * m) : 0,
  };
}

export const MAX_LEVEL = 18;
// XP per jungle camp: ceil(18360 / 42 layers) = 438, so 42 jungle camps → exactly level 18.
export const JUNGLE_XP_PER_LAYER = 438;

// LoL XP curve: cumulative XP to reach each level.
// Gap to next level starts at 280 and increases by 100 each level.
export const XP_FOR_LEVEL: readonly number[] = [
  0,      // level 1
  280,    // level 2
  660,    // level 3
  1140,   // level 4
  1720,   // level 5
  2400,   // level 6
  3180,   // level 7
  4060,   // level 8
  5040,   // level 9
  6120,   // level 10
  7300,   // level 11
  8580,   // level 12
  9960,   // level 13
  11440,  // level 14
  13020,  // level 15
  14700,  // level 16
  16480,  // level 17
  18360,  // level 18
];

export function levelForXp(xp: number): number {
  let level = 1;
  for (let i = 1; i < XP_FOR_LEVEL.length; i++) {
    if (xp >= XP_FOR_LEVEL[i]) level = i + 1;
  }
  return Math.min(level, MAX_LEVEL);
}

/** XP cost to reach the next level from current XP total. Returns 0 if already max level. */
export function xpToNextLevel(currentXp: number): number {
  const level = levelForXp(currentXp);
  if (level >= MAX_LEVEL) return 0;
  return XP_FOR_LEVEL[level] - currentXp;
}
